import { useState } from "react";
import Modal from "@/components/base/Modal";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import {
  useTeacherSchedule,
  WEEK_DAYS,
  SCHEDULE_TYPES,
  SUBJECTS,
} from "@/hooks/useTeacherContent";

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 bg-background-50";

const typeColors: Record<string, string> = {
  ders: "bg-accent-100 text-accent-800",
  canli: "bg-red-50 text-red-700",
  test: "bg-secondary-100 text-secondary-800",
};

const typeLabels: Record<string, string> = {
  ders: "Ders",
  canli: "Canlı Ders",
  test: "Test",
};

interface SlotForm {
  day: string;
  time: string;
  subject: string;
  type: string;
}

const emptyForm: SlotForm = { day: WEEK_DAYS[0], time: "09:00", subject: SUBJECTS[0], type: "ders" };

export default function DersProgramim() {
  const { data, loading, error, reload, addSlot, deleteSlot } = useTeacherSchedule();
  const slots = data ?? [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SlotForm>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!form.day || !form.time || !form.subject) {
      setFormError("Lütfen tüm alanları doldurun.");
      return;
    }
    setBusy(true);
    try {
      await addSlot(form);
      setShowForm(false);
      setForm(emptyForm);
    } catch {
      setFormError("Program kaydedilirken bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    setBusy(true);
    try {
      await deleteSlot(confirmId);
      setConfirmId(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="Ders Programım"
          description="Haftalık ders programını planla ve düzenle."
        />
        <button
          type="button"
          onClick={() => {
            setForm(emptyForm);
            setFormError(null);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line text-base" aria-hidden="true"></i>
          Ders Ekle
        </button>
      </div>

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : slots.length === 0 ? (
        <EmptyState
          icon="ri-calendar-line"
          title="Henüz ders planı oluşturulmadı"
          description="Haftalık programına ders eklemek için 'Ders Ekle' butonuna tıkla."
        />
      ) : (
        <div className="space-y-3">
          {WEEK_DAYS.map((day) => {
            const daySlots = slots.filter((s) => s.day === day).sort((a, b) => a.time.localeCompare(b.time));
            if (daySlots.length === 0) return null;
            return (
              <div key={day} className="rounded-xl border border-background-200 bg-background-50 p-4">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                  <i className="ri-calendar-2-line text-foreground-400" aria-hidden="true"></i>
                  {day}
                  <span className="text-xs font-normal text-foreground-400">{daySlots.length} ders</span>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {daySlots.map((slot) => (
                    <div
                      key={slot.id}
                      className="inline-flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 pl-3 pr-1.5 py-1.5"
                    >
                      <span className="text-xs font-semibold text-foreground-700">{slot.time}</span>
                      <span className="text-sm text-foreground-800">{slot.subject}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${typeColors[slot.type] ?? "bg-background-100 text-foreground-600"}`}>
                        {typeLabels[slot.type] ?? slot.type}
                      </span>
                      <button
                        type="button"
                        onClick={() => setConfirmId(slot.id)}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-foreground-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                        aria-label="Dersi sil"
                        title="Sil"
                      >
                        <i className="ri-close-line" aria-hidden="true"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ders formu */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Programa Ders Ekle"
        titleId="schedule-form-title"
        id="schedule-form-modal"
        maxWidthClass="max-w-md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Gün</label>
              <select
                value={form.day}
                onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))}
                className={inputClass}
              >
                {WEEK_DAYS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Saat</label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ders / Konu</label>
            <select
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              className={inputClass}
            >
              {SUBJECTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tür</label>
            <div className="grid grid-cols-3 gap-2">
              {SCHEDULE_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type: t.value }))}
                  className={`h-10 rounded-md text-sm font-medium border transition-colors whitespace-nowrap ${
                    form.type === t.value
                      ? "border-primary-400 bg-primary-100 text-primary-800"
                      : "border-background-200 text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {formError && <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{formError}</p>}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Ekleniyor..." : "Ekle"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Silme onayı */}
      <Modal
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        title="Dersi Programdan Kaldır"
        titleId="delete-slot-title"
        id="delete-slot-modal"
        maxWidthClass="max-w-sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground-600">Bu dersi programından kaldırmak istediğine emin misin?</p>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirmId(null)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="h-10 px-4 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Siliniyor..." : "Kaldır"}
            </button>
          </div>
        </div>
      </Modal>

      <PanelLinks
        links={[
          { label: "Derslerim", href: "/ogretmen/derslerim", icon: "ri-book-2-line" },
          { label: "Canlı Dersler", href: "/ogretmen/canli-dersler", icon: "ri-vidicon-line" },
        ]}
      />
    </div>
  );
}